import React, {useState, useEffect, useRef} from 'react';
import {Steps} from "primereact/steps";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {Dialog} from "primereact/dialog";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {Loading} from "../../utils/InlineComponents";
import CopyToClipboard from "../../utils/CopyToClipboard";
import {user, isAdmin} from "../../utils/Utils";
import {useOnboardingState} from "./useOnboardingState";
import {stepsForUsageType, STEP_CATALOG} from "./onboardingConfig";
import OnboardingMonitor from "./OnboardingMonitor";
import OnboardingStepContent from "./OnboardingStepContent";
import SettingsOnboardingStep from "./SettingsOnboardingStep";
import ScheduledJobsOnboardingStep from "./ScheduledJobsOnboardingStep";
import SpecialFieldsOnboardingStep from "./SpecialFieldsOnboardingStep";
import CleanUpOnboardingStep from "./CleanUpOnboardingStep";

const OnboardingPage = () => {
    const {t} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;
    // Marketplace API campaigns replace the legacy scraping "products" step
    // with "integration" (see onboardingConfig.js stepsForUsageType) -- the
    // page's default/reset position must follow suit, or it would silently
    // render the legacy Products step for these campaigns.
    const firstStepKey = selectedCampaign?.settings?.marketplace_api ? 'integration' : 'products';

    const {status, loading, lastUpdated, error, refresh, setStep, restart} = useOnboardingState(campaignId);

    // Wizard position (local). Always starts at the first step; the user
    // navigates manually from there (we don't auto-jump to backend state).
    const [activeKey, setActiveKey] = useState(firstStepKey);
    // On a cold load of #/onboarding (refresh / bookmark / deep-link),
    // CampaignContext's selectedCampaign is still null on first render (App.js
    // restores it asynchronously from localStorage after `campaigns` loads --
    // see App.js), so the useState seed above captures the wrong firstStepKey
    // ('products') for a marketplace campaign. Re-seed activeKey exactly once
    // when campaignId transitions from unset to set, and again on an actual
    // campaign switch (topbar selector) -- but never on a mere re-render for
    // the SAME campaign, so this must not clobber manual step navigation.
    const seededCampaignId = useRef(null);
    useEffect(() => {
        if (campaignId && seededCampaignId.current !== campaignId) {
            seededCampaignId.current = campaignId;
            setActiveKey(firstStepKey);
        }
    }, [campaignId, firstStepKey]);
    // Site focused when jumping from the Sites step into Site Definition (prepare).
    const [focusWebsite, setFocusWebsite] = useState(null);
    // Campaign Settings / Scheduled Jobs are always-available config (header icons, not flow steps).
    const [showSettings, setShowSettings] = useState(false);
    const [showScheduled, setShowScheduled] = useState(false);
    const [showSpecialFields, setShowSpecialFields] = useState(false);
    const [showCleanUp, setShowCleanUp] = useState(false);

    // Open the Site Definition (prepare) step focused on a given site.
    const openSiteDefinition = (website) => {
        setFocusWebsite(website || null);
        setActiveKey('prepare');
    };

    if (!campaignId) {
        return (
            <div className="p-grid p-fluid" style={{padding: 24}}>
                <Message severity="warn" text={t('onboarding.noCampaign', 'Önce bir kampanya seçin.')}/>
            </div>
        );
    }

    // Steps are dynamic per usage_type (backend mirrors this):
    //   manufacturer -> +map, seller -> +pricing.
    const usageType = status?.usage_type ?? selectedCampaign?.settings?.usage_type;
    const campaignSettings = selectedCampaign?.settings || {};
    const admin = isAdmin();
    // "prepare" (Site Definition / probes) and "search" are admin/technical
    // tooling — hidden from end users so they see a simpler flow.
    const END_USER_HIDDEN_STEPS = ['prepare', 'search'];
    const stepDefs = stepsForUsageType(usageType, campaignSettings).filter(s => admin || !END_USER_HIDDEN_STEPS.includes(s.key));
    const stepKeys = stepDefs.map(s => s.key);
    const idxOf = (key) => Math.max(0, stepKeys.indexOf(key));

    const activeIndex = idxOf(activeKey);
    // Distinct color per step so the rail reads as a colorful progress map.
    const STEP_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#d97706', '#db2777', '#16a34a', '#dc2626', '#0d9488'];
    const stepModel = stepDefs.map((s, index) => {
        const label = t(s.labelKey, s.labelDefault);
        const color = STEP_COLORS[index % STEP_COLORS.length];
        return {
            label: label,
            icon: s.icon,
            // Bigger colored icon with the step name underneath.
            template: (item, options) => (
                <a href="#" className={options.className} onClick={options.onClick}
                   tabIndex={options.tabIndex} title={label}
                   style={{flexDirection: 'column', alignItems: 'center', background: 'transparent'}}>
                    <span className={options.numberClassName}
                          style={{color: color, borderColor: color, width: '3rem', height: '3rem'}}>
                        <i className={item.icon} style={{color: color, fontSize: '1.6rem'}}/>
                    </span>
                    <span style={{marginTop: 8, fontSize: '.82rem', fontWeight: options.active ? 700 : 500,
                                  color: options.active ? color : '#374151', textAlign: 'center',
                                  maxWidth: '8em', whiteSpace: 'normal', lineHeight: 1.2}}>
                        {label}
                    </span>
                </a>
            ),
        };
    });

    const goToKey = (key) => {
        setFocusWebsite(null);   // manual navigation clears the Site Definition site focus
        setActiveKey(key);
    };

    const goNext = () => {
        const next = stepKeys[Math.min(stepKeys.length - 1, activeIndex + 1)];
        goToKey(next);
        // Persist forward progress (don't move the pointer backwards).
        if (idxOf(next) > idxOf(status?.state || 'products')) setStep(next);
    };
    const goBack = () => goToKey(stepKeys[Math.max(0, activeIndex - 1)]);

    const onRestart = () => {
        restart().then(() => setActiveKey(firstStepKey));
    };

    const isLast = activeIndex === stepKeys.length - 1;
    const isReady = status?.state === 'ready';

    return (
        <div className="onboarding-page" style={{padding: 16}}>
            {/* top status bar */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
                         background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 20px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 14, minWidth: 0}}>
                    <span style={{width: 48, height: 48, borderRadius: '50%', background: '#eef2fb', color: '#2563eb',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        <i className="pi pi-cog" style={{fontSize: '1.5rem'}}/>
                    </span>
                    <div style={{minWidth: 0}}>
                        <div style={{fontSize: '.78rem', textTransform: 'uppercase', letterSpacing: '.06em',
                                     color: '#6b7280', fontWeight: 600}}>
                            {t('onboarding.title', 'Kurulum')}
                        </div>
                        <div style={{fontSize: '1.5rem', fontWeight: 800, color: '#111827', lineHeight: 1.15,
                                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {selectedCampaign?.name}
                        </div>
                        {admin &&
                        <div style={{display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap'}}>
                            <CopyToClipboard isButton buttonLabel={t('onboarding.campaignId', 'Kampanya ID')} textToCopy={campaignId}/>
                            <CopyToClipboard isButton buttonLabel={t('onboarding.userId', 'Kullanıcı ID')} textToCopy={selectedCampaign?.user_id || (user() && user().id)}/>
                        </div>}
                    </div>
                </div>
                <span style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    {admin && <Button icon={STEP_CATALOG.settings.icon} className="p-button-rounded p-button-lg"
                            style={{width: '3rem', height: '3rem', fontSize: '1.35rem', color: '#fff', border: 'none',
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                boxShadow: '0 4px 12px rgba(99,102,241,.35)'}}
                            tooltip={t(STEP_CATALOG.settings.labelKey, STEP_CATALOG.settings.labelDefault)}
                            tooltipOptions={{position: 'bottom'}} onClick={() => setShowSettings(true)}/>}
                    {admin && <Button icon={STEP_CATALOG.scheduled_jobs.icon} className="p-button-rounded p-button-lg"
                            style={{width: '3rem', height: '3rem', fontSize: '1.35rem', color: '#fff', border: 'none',
                                background: 'linear-gradient(135deg, #06b6d4 0%, #14b8a6 100%)',
                                boxShadow: '0 4px 12px rgba(20,184,166,.35)'}}
                            tooltip={t(STEP_CATALOG.scheduled_jobs.labelKey, STEP_CATALOG.scheduled_jobs.labelDefault)}
                            tooltipOptions={{position: 'bottom'}} onClick={() => setShowScheduled(true)}/>}
                    {admin && <Button icon="pi pi-tags" className="p-button-rounded p-button-lg"
                            style={{width: '3rem', height: '3rem', fontSize: '1.35rem', color: '#fff', border: 'none',
                                background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                                boxShadow: '0 4px 12px rgba(245,158,11,.35)'}}
                            tooltip={t('campaign.special_field_setting', 'Özel Alanlar')}
                            tooltipOptions={{position: 'bottom'}} onClick={() => setShowSpecialFields(true)}/>}
                    {admin && <Button icon="pi pi-trash" className="p-button-rounded p-button-lg"
                            style={{width: '3rem', height: '3rem', fontSize: '1.35rem', color: '#fff', border: 'none',
                                background: 'linear-gradient(135deg, #ef4444 0%, #f43f5e 100%)',
                                boxShadow: '0 4px 12px rgba(239,68,68,.35)'}}
                            tooltip={t('clean_up.clean_up', 'Temizlik')}
                            tooltipOptions={{position: 'bottom'}} onClick={() => setShowCleanUp(true)}/>}
                    {isReady && (
                        <Button label={t('onboarding.rerun')}
                                icon="pi pi-replay" className="p-button-sm p-button-outlined p-button-secondary" onClick={onRestart}/>
                    )}
                </span>
            </div>

            {error && (
                <div style={{marginBottom: 12}}>
                    <Message severity="error" text={t('onboarding.error', 'Durum alınamadı. Yenilemeyi deneyin.')}/>
                </div>
            )}

            {/* step rail */}
            <div style={{background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 16px', marginBottom: 20}}>
                <Steps model={stepModel} activeIndex={activeIndex} readOnly={false}
                       onSelect={(e) => goToKey(stepKeys[e.index])}/>
            </div>

            {/* content + persistent monitor */}
            <div className="p-grid" style={{display: 'flex', gap: 20, flexWrap: 'wrap'}}>
                <div style={{flex: '1 1 420px', minWidth: 320, border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, background: '#fff'}}>
                    {loading
                        ? <div style={{textAlign: 'center', padding: '4em 1em'}}>
                            <Loading/>
                            <div style={{marginTop: 12, color: '#6b7280'}}>{t('onboarding.loading', 'Yükleniyor…')}</div>
                        </div>
                        : <OnboardingStepContent stepKey={activeKey} t={t} status={status} refresh={refresh}
                                                 onOpenSiteDefinition={openSiteDefinition} focusWebsite={focusWebsite}
                                                 campaignId={campaignId}/>}

                    {/* navigation */}
                    <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 28}}>
                        <Button label={t('onboarding.nav.back', 'Geri')} icon="pi pi-chevron-left"
                                className="p-button-text" onClick={goBack} disabled={activeIndex === 0}/>
                        <span style={{display: 'flex', gap: 8}}>
                            {!isLast && (
                                <Button label={t('onboarding.nav.next', 'İleri')}
                                        icon="pi pi-chevron-right" iconPos="right" onClick={goNext}/>
                            )}
                        </span>
                    </div>
                </div>

                <div style={{flex: '0 0 320px', minWidth: 300}}>
                    <OnboardingMonitor status={status} loading={loading} lastUpdated={lastUpdated}
                                       onRefresh={refresh} t={t} currentStep={activeKey} onStepClick={goToKey}
                                       steps={stepDefs.filter(s => s.key !== 'price_check')}/>
                </div>
            </div>

            <Dialog header={t(STEP_CATALOG.settings.labelKey, STEP_CATALOG.settings.labelDefault)} visible={showSettings}
                    style={{width: '960px', maxWidth: '96vw'}} modal onHide={() => setShowSettings(false)}>
                <SettingsOnboardingStep t={t} status={status} refresh={refresh} embedded/>
            </Dialog>
            <Dialog header={t(STEP_CATALOG.scheduled_jobs.labelKey, STEP_CATALOG.scheduled_jobs.labelDefault)} visible={showScheduled}
                    style={{width: '960px', maxWidth: '96vw'}} modal onHide={() => setShowScheduled(false)}>
                <ScheduledJobsOnboardingStep t={t} status={status} refresh={refresh} embedded/>
            </Dialog>
            <Dialog header={t('campaign.special_field_setting', 'Özel Alanlar')} visible={showSpecialFields}
                    style={{width: '560px', maxWidth: '95vw'}} modal onHide={() => setShowSpecialFields(false)}>
                <SpecialFieldsOnboardingStep t={t} refresh={refresh}/>
            </Dialog>
            <Dialog header={t('clean_up.clean_up', 'Temizlik')} visible={showCleanUp}
                    style={{width: '960px', maxWidth: '96vw'}} modal onHide={() => setShowCleanUp(false)}>
                <CleanUpOnboardingStep t={t}/>
            </Dialog>
        </div>
    );
};

export default OnboardingPage;
